import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { BRAND } from '@/data/mockData';
import { NAV } from '@/constants/testIds';

const links = [
  { to: '/', label: 'Home', tid: NAV.navHome },
  { to: '/programs', label: 'Programs', tid: NAV.navPrograms },
  { to: '/success-stories', label: 'Reviews', tid: NAV.navSuccess },
  { to: '/learning-hub', label: 'Learning Hub', tid: NAV.navLearningHub },
  { to: '/about', label: 'About', tid: NAV.navAbout },
  { to: '/gallery', label: 'Gallery', tid: NAV.navGallery },
  { to: '/feedback', label: 'Feedback', tid: NAV.navFeedback },
  { to: '/contact', label: 'Contact', tid: NAV.navContact },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-[#050A14]/80 border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logoLink} className="flex items-center gap-3 group">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-brand-cyan/30 blur-xl group-hover:bg-brand-cyan/50 transition" />
            <img
              src={BRAND.logoUrl}
              alt="BYMAT Automation"
              className="relative h-12 w-12 object-contain rounded-lg bg-black"
            />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-lg font-black tracking-tight text-white">BYMAT</div>
            <div className="text-[10px] tracking-[0.3em] text-brand-cyan/80 font-mono uppercase -mt-0.5">Automation</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.tid}
              end={l.to === '/'}
              className={({ isActive }) =>
                `relative px-3 py-2 text-sm font-medium transition ${
                  isActive ? 'text-brand-cyan' : 'text-white/80 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{l.label}</span>
                  {isActive && (
                    <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-brand-cyan shadow-[0_0_10px_rgba(0,240,255,0.8)]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid={NAV.navCtaConsult}
            className="hidden md:inline-flex items-center gap-2 bg-brand-cyan text-black font-semibold px-4 py-2 rounded-lg text-sm btn-glow hover:bg-brand-cyanSoft transition"
          >
            Book Consultation
            <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid={NAV.mobileMenuToggle}
            className="lg:hidden h-10 w-10 rounded-lg glass flex items-center justify-center text-white"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden glass-strong border-t border-white/5">
          <div className="px-4 py-4 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-${l.tid}`}
                end={l.to === '/'}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-lg text-sm ${
                    isActive ? 'bg-brand-cyan/10 text-brand-cyan' : 'text-white/80 hover:bg-white/5'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="block mt-3 text-center bg-brand-cyan text-black font-semibold px-4 py-3 rounded-lg btn-glow"
            >
              Book Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
